// Uploads de documentos do self assistant que ainda estao sendo processados.
//
// O processamento roda numa Function Durable: o backend responde 202 com um
// `status_url` e quem acompanha o job ate o `Completed` (e entao registra o arquivo
// em `database`, via updateDocumentList + autosave) e' o proprio frontend. O estado
// desse acompanhamento vivia so no componente, entao sair da tela antes do fim
// perdia o job de vista: o arquivo era indexado, mas nunca aparecia na lista.
//
// Aqui ficam (1) a pendencia guardada em sessionStorage, por assistente, e (2) a
// consulta de status. sessionStorage de proposito: some ao fechar a aba, e o
// `status_url` do Durable Functions pode carregar uma chave na propria URL.

export const PENDING_UPLOAD_TTL_MS = 30 * 60 * 1000;

const STORAGE_KEY = 'selfassistant_pending_uploads';
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 600; // ~30 min, igual ao TTL da pendencia

// Formato: { [assistantId]: [{ filename, status_url, started_at }] }
const readAll = () => {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const writeAll = (all) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    } catch {
        // storage indisponivel/cheio: o upload segue normal, so perde a retomada
    }
};

const isFresh = (entry, now) =>
    Boolean(entry?.filename && entry?.status_url) && now - Number(entry.started_at) < PENDING_UPLOAD_TTL_MS;

export const listPendingUploads = (assistantId, now = Date.now()) => {
    const entries = readAll()[assistantId];
    return Array.isArray(entries) ? entries.filter(entry => isFresh(entry, now)) : [];
};

export const addPendingUpload = (assistantId, { filename, statusUrl }, now = Date.now()) => {
    const all = readAll();
    const others = listPendingUploads(assistantId, now).filter(entry => entry.filename !== filename);
    all[assistantId] = [...others, { filename, status_url: statusUrl, started_at: now }];
    writeAll(all);
};

export const removePendingUpload = (assistantId, filename) => {
    const all = readAll();
    if (!Array.isArray(all[assistantId])) return;
    const remaining = all[assistantId].filter(entry => entry.filename !== filename);
    if (remaining.length > 0) {
        all[assistantId] = remaining;
    } else {
        delete all[assistantId];
    }
    writeAll(all);
};

// Consulta o job ate ele terminar. Devolve o status quando `Completed`; lanca erro
// (com `terminal: true` se o job falhou/foi cancelado) caso contrario. Devolve `null`
// se `shouldContinue()` passar a false (ex.: a tela foi desmontada) - quem chamou
// decide o que fazer, e a pendencia continua guardada para a retomada.
export const pollJobStatus = async (
    statusUrl,
    accessToken,
    shouldContinue = () => true,
    { intervalMs = POLL_INTERVAL_MS, maxAttempts = POLL_MAX_ATTEMPTS } = {},
) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, intervalMs));

        if (!shouldContinue()) return null;

        const statusResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/get_job_status?status_url=${encodeURIComponent(statusUrl)}`,
            {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}` },
            }
        );

        if (!statusResponse.ok) {
            throw new Error(`Status check falhou: ${statusResponse.status}`);
        }

        const statusData = await statusResponse.json();
        const runtime = statusData.runtimeStatus;

        if (runtime === 'Completed') {
            return statusData;
        }
        if (runtime === 'Failed' || runtime === 'Terminated' || runtime === 'Canceled') {
            const error = new Error(`Job ${runtime}`);
            error.terminal = true;
            throw error;
        }
    }
    throw new Error('Timeout aguardando conclusão do job');
};
